import { Controller } from '@hotwired/stimulus';

// Rows of a nested form (accepts_nested_attributes_for), one for every host.
//
//   <div data-controller="nested">
//     <div data-nested-target="rows">
//       <%= f.fields_for :rooms do |r| %> <div data-nested-row> … <% end %>
//     </div>
//     <template data-nested-target="template">
//       <%= f.fields_for :rooms, Room.new, child_index: "NEW_RECORD" do |r| %> … <% end %>
//     </template>
//     <button type="button" data-action="nested#add">+</button>
//   </div>
//
// add:    the template's NEW_RECORD becomes a fresh index, appended to rows.
// remove: (inside a [data-nested-row]) a saved row flips its _destroy input
//         and hides, because only _destroy deletes it; a row never saved
//         (no _destroy input, or data-new-record="true") is dropped.
//
// The focus follows (docs/HOTWIRE.md): add lands on the new row's first
// SHOWN field (no field: its first shown control); remove on the next shown row's, else
// the previous one's, else the add button. A keyboard never falls back to
// <body>. A row may name its own landing with data-nested-focus on one of its
// elements; shown, it wins over the first field.
//
// Both dispatch — nested:added / nested:removed, detail { row } — for a
// neighbour that cares (zaz's map drops the pin of a removed room).
const FIELD = 'input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])';
const CONTROL = `${FIELD}, button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])`;
const shown = (row, sel) => [...(row?.querySelectorAll(sel) || [])].find((el) => el.checkVisibility({ visibilityProperty: true }));
// A row may name its landing (data-nested-focus): a shut menu row's summary face.
const first = (row) => shown(row, '[data-nested-focus]') || shown(row, FIELD) || shown(row, CONTROL);

export default class extends Controller {
  static targets = ['rows', 'template'];

  add(event) {
    event?.preventDefault();
    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, `${Date.now()}`);
    this.rowsTarget.insertAdjacentHTML('beforeend', html);
    const row = this.rowsTarget.lastElementChild;
    this.dispatch('added', { detail: { row } });
    first(row)?.focus();
  }

  remove(event) {
    event?.preventDefault();
    const row = event.target.closest('[data-nested-row]');
    if (!row) return;

    const shown = [...this.rowsTarget.querySelectorAll('[data-nested-row]')].filter((r) => r === row || !r.hidden);
    const at = shown.indexOf(row);
    const heir = shown[at + 1] || shown[at - 1];

    const destroy = row.querySelector("input[name*='_destroy']");
    if (destroy && row.dataset.newRecord !== 'true') {
      destroy.value = '1';
      row.hidden = true;
    } else {
      row.remove();
    }
    this.dispatch('removed', { detail: { row } });
    (first(heir) || this.element.querySelector("[data-action*='nested#add']"))?.focus();
  }
}
